import {
	AnyThreadChannel,
	GuildForumTag,
	NewsChannel,
	TextChannel,
} from "discord.js";
import { RRule } from "rrule";
import { URLSearchParams } from "url";
import { RouterInferenceHelpers } from "../src/api/trpc/rootRouter";
import { EventEntity } from "../src/entity/Event";
import type { GuildEntity } from "../src/entity/Guild";
import type { PollVoteEntity } from "../src/entity/PollVote";
import { ServerReminderDescriptor } from "../src/entity/ServerReminder";
import { RDateDescriptor } from "./repeat_utils";

export type GuildTextChannelNonThread = TextChannel | NewsChannel;
export type GuildTextChannel = GuildTextChannelNonThread | AnyThreadChannel;

export enum SeshThreadAutoArchiveDuration {
	OneHour,
	OneDay,
	ThreeDays,
	OneWeek,
}

export enum PollType {
	Standard,
	Time,
}

export enum SyncInclusionType {
	All,
	Channel,
	RSVP,
}

export enum SyncDirection {
	Outbound,
	Inbound,
	Bidirectional,
}

export function syncDirectionToName(syncDirection: SyncDirection) {
	switch (syncDirection) {
		case SyncDirection.Outbound:
			return "Outbound";
		case SyncDirection.Inbound:
			return "Inbound";
		case SyncDirection.Bidirectional:
			return "Bidirectional";
	}
}

export type NativeEventLocationType =
	| "EVENT_LINK"
	| "VOICE_CHANNEL"
	| "STAGE_CHANNEL"
	| "ELSEWHERE";

export interface NativeEventLocation {
	type: NativeEventLocationType;
	value?: string;
}

export interface NativeEventData {
	location: NativeEventLocation;
	custom_description?: string;
	image_url?: string;
}

interface SeshChannelData {
	id: string;
	name: string;
	position?: number;
	category_id?: string;
	category_name?: string;
	category_position?: number;
}

export interface GuildDefaultEventSettings {
	native_event_data?: NativeEventData;
	attendee_role_data?: AttendeeRoleDataWithIDs;
	hide_event_attendees?: boolean;
}

export type WebChannelTypes = "Text" | "Thread" | "Forum";

type SeshTextChannelDataUntyped<T extends WebChannelTypes = WebChannelTypes> =
	SeshChannelData & {
		type: T;
		has_write: boolean;
		can_make_public_threads: boolean;
		overrides?: GuildEntityColumns;
	} & (T extends "Forum"
			? {
					tag_settings: {
						tags: GuildForumTag[];
						required: boolean;
					};
			  }
			: {});

type SeshTextChannelTypedRecord = {
	[P in WebChannelTypes]: SeshTextChannelDataUntyped<P>;
};
export type SeshTextChannelData<
	Subset extends keyof SeshTextChannelTypedRecord = keyof SeshTextChannelTypedRecord
> = SeshTextChannelTypedRecord[keyof SeshTextChannelTypedRecord & Subset];

export interface SeshVoiceChannelData extends SeshChannelData {
	is_stage: boolean;
	sesh_missing_perms: string[];
	user_missing_perms: string[];
}

export type EventDeleteType = "Single" | "Single_And_Future";

export interface GuildData {
	id: string;
	name: string;
	icon?: string;
	has_sesh?: boolean;
	is_user_admin?: boolean;
	is_premium?: boolean;
	is_discord_premium?: boolean;
}

export interface VoteData {
	title: string;
	votes: number;
	percentage: number;
	voter_names?: string[];
	voter_ids?: string[];
}

export interface PollVoteData {
	voters: { [key: string]: PollVoteEntity }[];
	vote_counts: number[];
	total_vote_count: number;
}

// user_vote_poll_thing_entity
// 		voter / poll_id / option_index
// 	do not store vote_counts, just query count
// https://typeorm.io/#/many-to-many-relations/many-to-many-relations-with-custom-properties

export interface DiscordAuthToken {
	access_token: string;
	token_type: string;
}

export interface GuildSettingsSetRequest {
	settings: GuildSettings;
	access_token: string;
	token_type: string;
	guild_id: string;
}

export interface ChannelOverrideSettingsSetRequest {
	settings: GuildEntityColumns;
	access_token: string;
	token_type: string;
	guild_id: string;
	channel_id: string;
}

export interface ResetCalendarFeedsRequest {
	guild_id: string;
}

export interface ChannelOverrideSettingsDeleteRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
	channel_id: string;
}
export interface GetPollResultsRequest {
	message_id: string;
	guild_id: string;
	access_token: string;
	token_type: string;
}

export interface GetGuildIDFromPollIDRequest {
	access_token: string;
	token_type: string;
	message_id: string;
}

export interface SetGuildTimezoneRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
	timezone: string;
	channel_id?: string;
}

export interface ThreadSettings {
	name: string;
	auto_archive_duration: SeshThreadAutoArchiveDuration;
	should_auto_join_attendees_on_rsvp: boolean;
	should_send_start_message_to_thread: boolean;
}

export interface SetSecondaryTimezoneRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
	timezone: string;
	removing: boolean;
	channel_id?: string;
}

export interface GetGuildMembersResult {
	members: Mentionable[];
	longest_name_length: number;
	err?: string;
}
export interface GuildSettingsProps {
	guild_data: GuildData;
	roles: Mentionable[];
	all_text_channels: SeshTextChannelData[];
	all_voice_channels: SeshVoiceChannelData[];
	is_admin: boolean;
	is_premium: boolean;
	all_guild_data: GuildData[];
	user_id: string;
	sesh_has_manage_roles: boolean;
	sesh_has_manage_events: boolean;
	user_has_manage_events: boolean;
	sesh_role_name: string;
	premium_activator_id?: string;

	initial_event_channel_id?: string;
	initial_poll_channel_id?: string;

	original_guild_settings: GuildSettings;

	clear_parents_settings_data_func?: Function;
	on_save_func?: (
		settings: GuildSettings,
		individual_channel_settings_overrides?: GuildEntityColumns,
		channel_id?: string,
		guild_id?: string,
		refetch_channels?: boolean
	) => void;

	on_premium_modified_func?: (props: GuildSettingsProps) => void;

	err?: string;
	auth_token?: DiscordAuthToken;
	fragment?: URLSearchParams;

	is_channel_settings_overrides_page?: boolean;
	individual_channel_id?: string;
	individual_channel_settings_overrides?: GuildEntityColumns;
}

export type ChannelID = string;

type ExcludeMethods<T> = Pick<
	T,
	{ [K in keyof T]: T[K] extends Function ? never : K }[keyof T]
>;

export type GuildEntityColumnsNoPartial = ExcludeMethods<GuildEntity>;
export type GuildEntityColumns = Partial<ExcludeMethods<GuildEntity>>;
export interface GuildSettings {
	prefix: string;
	timezone: string;
	signup_options: OptionPreset;
	event_channel: SeshTextChannelData;
	event_channel_override_only_admins: boolean;
	event_channel_require_send_messages: boolean;
	event_channel_forum_tag_ids: string[];
	poll_channel: SeshTextChannelData;
	poll_channel_override_only_admins: boolean;
	poll_channel_require_send_messages: boolean;
	poll_channel_forum_tag_ids: string[];
	poll_add: boolean;
	use_24h_clock: boolean;
	clean_event_messages: boolean;
	clean_user_commands: boolean;
	permissions: CommandPermissions[];
	display_attendees_as_plain_text: boolean;
	secondary_timezones: string[];
	attendee_role_data: AttendeeRoleDataWithIDs;
	is_default_poll_type_single_vote: boolean;
	default_hide_attendees: boolean;
	use_discord_timestamps: boolean;
	use_channel_calendars_mode: boolean;
	use_reactions_for_polls: boolean;
	default_event_thread_settings: ThreadSettings | null;
	default_event_server_reminders: ServerReminderDescriptor[];
	default_user_mentions_on_event_create: string[];
	default_role_mentions_on_event_create: string[];
	is_ai_enabled: boolean;

	default_poll_embed_color: string | null;
	default_event_embed_color: string | null;

	default_native_event_data: NativeEventData | null;

	guild_log_channel_id: string | null;
	guild_log_channel_fail_streak: number;
}
export interface GuildChannelSettingsOverrides {
	// channel_id -> {key -> value}
	overrides: Record<ChannelID, GuildEntityColumns>;
}

export interface SetGuildPresetRequest {
	guild_id: string;
	access_token: string;
	token_type: string;
	preset: OptionPreset;
	channel_id?: string;
}

export interface SetGuildPermissionsRequest {
	guild_id: string;
	access_token: string;
	token_type: string;
	permissions: CommandPermissions[];
	channel_id?: string;
}

export interface GetServerSettingsRequest {
	guild_id: string;
	access_token: string;
	token_type: string;
}

export const GuildSettingsSectionNames = [
	"General",
	"Event",
	"Poll",
	"Sync",
	"Logs",
	"Premium",
] as const;

export type GuildSettingsSectionType =
	(typeof GuildSettingsSectionNames)[number];

export enum WebDashboardType {
	Guild_Selection,
	Event,
	Signup_Presets,
	Poll,
	Poll_Results,
	Server_Settings,
	Redirect,
}

export enum RoleOnRSVPAddTime {
	OnRSVP,
	OnEventStart,
}

export interface RawEventRSVPOption {
	emoji: string;
	name: string;
	has_reminders: boolean;
	limit?: number;
	user_ids: string[];
	just_added?: boolean;
	removed?: boolean;
	do_not_apply_attendee_role?: boolean;

	custom_emoji_data?: CustomEmojiData;
}

export interface PollVoteInteractionData {
	user_id: string;
	index: number;
}

export interface PollReactionData {
	user_id: string;
	emoji: string;
	reaction_event_type: ReactionEventType;
}

export enum ReactionEventType {
	Add,
	Remove,
}

export interface OptionPreset {
	name: string;
	author_id?: string;
	unsaved_changes?: boolean;
	custom_options: RawEventRSVPOption[];
	allow_multiple_rsvps: boolean;
}

export interface CustomEmojiData {
	guild_id: string;
	guild_name: string;
	name: string;
	id: string;
	animated: boolean;
}

export interface IsUserAdminRequest {
	guild_id: string;
}

export interface ModifyGuildActivationRequest {
	guild_id: string;
	is_activate: boolean;
}

export type ModifyPremiumResult =
	| {
			result: {
				current_activations: number;
				max_activations: number;
			};
	  }
	| { err: string };

export type ModifyPremiumStatusWebResponse = {
	result?: {
		current_activations: number;
		max_activations: number;
	};
	guild_settings_props: GuildSettingsProps | null;
	err?: string;
};

export interface IsUserAdminResponse {
	is_admin: boolean;
}

export interface CommandPermissions {
	command: string;
	roles_ids: string[];
}

export interface SignUpPresetsProp {
	is_premium: boolean;
	user_presets: OptionPreset[];
	user_id: string;
	custom_emojis: CustomEmojiData[];
	all_guild_data: GuildData[];
	dm_data?: IGetGuildDefaultOptionsFromDMArgs;
	auth_token?: DiscordAuthToken;

	err?: string;
}

export interface GetGuildDataRequest {
	access_token: string;
	token_type: string;
}

export interface DashboardResponse<T> {
	props: T;
	err?: string;
}

export interface GuildSelectProps {
	auth_token?: DiscordAuthToken;
	bot_client_id: string;
	list_of_guilds: GuildData[];
}

export interface GetEventDataRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
	original_channel_id?: string;
	event_template_id?: string;
	message_id?: string;
}

export interface GetDataFromGuildRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
}

export interface GetGuildVoiceChannelsResult {
	channels: SeshVoiceChannelData[];
	err?: string;
}

export interface GetGuildRolesAndChannelsResult {
	roles: Mentionable[];
	channels: SeshTextChannelData[];
	voice_channels: SeshVoiceChannelData[];
	sesh_has_manage_roles: boolean;
	sesh_has_manage_events: boolean;
	sesh_has_create_public_threads: boolean;
	user_has_manage_events: boolean;
	sesh_role_name: string;
	err?: string;
}

export interface GetPollDataRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
	message_id?: string;
}

export interface ClearTokenRequest {
	access_token: string;
}

export interface GetListingsRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
}

export interface EventListingsFormProp {
	guild_data: GuildData;
	event_display_data: EventDisplayData[];
	auth_token: DiscordAuthToken;
	fragment: URLSearchParams;
}

export interface IDeleteEventResult {
	err?: string;
	db_success: boolean;
	repeat_delete_result?: {
		err?: string;
		new_entity?: EventEntity;
	};
}

export interface DeleteRequest {
	access_token: string;
	token_type: string;
	guild_id: string;
	message_id: string;
	delete_type?: EventDeleteType;
}

export interface IDeleteEventOrPollArgs {
	guild_id: string;
	message_id: string;
	deleter_id: string;
	is_admin: boolean;
}

export interface IDeleteEventArgs extends IDeleteEventOrPollArgs {
	delete_type?: EventDeleteType;
}

export type CSVWebResult = {
	err?: string;
	result: string[][];
};

export type EventDisplayDataCommon = {
	message_id: string;
	signup_message_id: string;
	description?: string;
	author_id: string;
	name: string;
	created_at_str: string;
	color: string;
	start_time_str: string;
	end_time_str: string;
	channel_id: string;
	rsvp_options: EventDisplayDataRSVPOption[];
	edit_version?: number;
};

export type EventDisplayData =
	| (EventDisplayDataCommon & {
			rrule: null;
			is_repeat_occurrence?: false;
			original_repeat_message_id: null;
	  })
	| (EventDisplayDataCommon & {
			rrule: string;
			original_repeat_message_id: string;
			is_repeat_occurrence?: boolean;
			/** The original start time of the canonical (current) repeat instance of the event */
			repeat_instance_original_start_time_str: string;
			repeat_tz: string;
			exdates: string[];
			rdates: RDateDescriptor[];
			/** The current start time of the canonical (current) event */
			initial_event_start_time_str?: string;
	  });

export type EventDisplayDataRSVPOption = {
	id: string;
	emoji: string;
	name: string;
	index: number;
	rsvp_count: number;
	limit?: number;
	custom_emoji_data?: CustomEmojiData;
};

export type ModifyRSVPersForEventResponse =
	| {
			guild_id: string;
	  }
	| { err: string };

export interface ModifyEventRSVPersRequest {
	event_id: string;
	guild_id: string;
	option_emoji: string;
	rsvpers_to_modify: string[];

	access_token: string;
	token_type: string;
}

export interface PollDisplayData {
	message_id: string;
	vote_embed_message_id: string;
	author_id: string;
	poll_name: string;
	created_at: string;
	end_time: string;
	channel_id: string;
	vote_count: number;
	type: PollType;
	is_anonymous: boolean;
}

export interface PollEditData {
	is_reaction_based: boolean;
	poll_message_id: string;
	poll_title: string;
	description: string;
	end_time?: Date;
	selected_channel_id: string;
	allow_anyone_to_add: boolean;
	color?: string;
	options?: string[];
	eligible_role_ids?: string[];
	image_url?: string;
	single_vote_per_user: boolean;
	is_anonymous: boolean;
	poll_type: PollType;
	role_mention_ids: string[];
	user_mention_ids: string[];
}

export type EventTemplateData = Partial<SerializedEvent>;

export interface SerializedEvent {
	author_id: string;
	event_message_id: string;
	event_name: string;
	start_time: Date;
	duration_ms: number | undefined;
	rrule_str: string | null;
	allow_maintain_repeat_rsvp: boolean;
	description: string;
	rsvp_options: OptionPreset;
	color: string | null;
	role_mentions_on_create: string[];
	user_mentions_on_create: string[];
	eligible_role_ids: string[];
	hide_attendees: boolean;
	image_url: string | null;
	attendee_role_data: AttendeeRoleDataWithIDs;
	sent_channel_data: { id: string; forum_tag_ids: string[] };
	event_timezone: string;
	native_event_data: NativeEventData | undefined;
	thread_settings: ThreadSettings | undefined;
	server_reminders?: ServerReminderDescriptor[];
	rsvp_lock_before_ms: number | null;
	exdates?: Date[];
	rdates?: RDateDescriptor[];
}

// | "event_name"
// 	| "description"
// 	| "selected_channel"
// 	| "start_time"
// 	| "end_time"
// 	| "rrule"
// 	| "rsvp_options"
// 	| "color"
// 	| "create_and_start_mentions"
// 	| "eligible_roles"
// 	| "hide_attendees"
// 	| "image_url"
// 	| "attendee_role_data"
// 	| "event_post_delay_ms"
// 	| "inform_users_of_edit"
// 	| "native_event_data";

export interface DropdownData {
	value: string;
	label: string | JSX.Element;
	selectedLabel?: JSX.Element;
	isDisabled?: boolean;
	mentionable?: Mentionable;
	color?: string;
}

export interface EventCreationFieldValues {
	event_name: string;
	description: string;

	start_time: luxon.DateTime;
	duration_ms: number;
	rrule: RRule;
	selected_channel: { id: string; forum_tag_ids: string[] };
	rsvp_options?: OptionPreset;
	color?: string;
	hide_attendees?: boolean;
	user_mentions_on_create: string[];
	role_mentions_on_create: string[];
	eligible_role_ids: string[];
	image_url: string;
	attendee_role_data: AttendeeRoleDataWithIDs;
	native_event_data?: NativeEventData;
	thread_settings?: ThreadSettings;
	server_reminders?: ServerReminderDescriptor[];
	allow_maintain_repeat_rsvp?: boolean;
	event_timezone: string;
	exdates: Date[];
	rdates: RDateDescriptor[];
	rsvp_lock_before_ms: number | null;
}

export type EventCreationStateFields = keyof EventCreationFieldValues;

export interface EventCreationState extends EventCreationFieldValues {
	is_edit: boolean;
	current_page: "main" | "templates";
	header_text: JSX.Element;
	guild_timezone: string;
	use_24h_clock: boolean;
	user_timezone?: string;
	can_make_recurring: boolean;
	placeholder_fields?: PlaceholderFields;
	original_start_time?: luxon.DateTime;
	user_presets?: OptionPreset[];
	custom_emojis?: CustomEmojiData[];

	guild_data_for_this_guild: GuildData;
	inform_users_of_edit: boolean;

	confirmation_data?: IWebConfirmationData;
	is_confirming: boolean;

	loading_roles_and_channels?: boolean;
	date_input?: string;
}

export interface PlaceholderFields {
	event_name: string;
	description: string;
	start_time: string;
	duration: string;
}

export type OnEventEditFunction = (
	guild_id: string,
	display_data: EventDisplayData
) => void;

export type OnPollEditFunction = (
	guild_id: string,
	display_data: PollDisplayData
) => void;

export interface EventCreationProps {
	user_id: string;
	guild_id: string;
	guild_name: string;

	edit_data?: SerializedEvent;
	template_data?: RouterInferenceHelpers["event_template"]["get"]["output"];

	on_event_edited_func?: OnEventEditFunction;

	guild_defaults: Pick<
		GuildSettings,
		| "attendee_role_data"
		| "default_hide_attendees"
		| "timezone"
		| "default_native_event_data"
		| "signup_options"
		| "default_event_thread_settings"
		| "default_event_embed_color"
		| "default_event_server_reminders"
		| "default_user_mentions_on_event_create"
		| "default_role_mentions_on_event_create"
	>;

	is_edit: boolean;
	event_channel_id: string | null;
	guild_timezone: string;
	use_24h_clock: boolean;
	default_hide_attendees: boolean;
	user_timezone: string | null;
	is_premium: boolean;
	can_make_recurring: boolean;
	can_select_channel: boolean;
	original_start_time?: Date;
	custom_emojis: CustomEmojiData[] | undefined;
	rsvp_options?: OptionPreset;
	user_presets?: OptionPreset[];
	all_guild_data: GuildData[];

	roles: Mentionable[];

	all_text_channels: SeshTextChannelData[];
	all_voice_channels: SeshVoiceChannelData[];
	sesh_has_manage_roles: boolean;
	sesh_has_manage_events: boolean;
	user_has_manage_events: boolean;
	sesh_role_name: string;

	err?: string;
	auth_token?: DiscordAuthToken;
	fragment?: URLSearchParams;

	on_unmount?: Function;
}
export interface PollCreationProps {
	user_id: string;
	guild_id: string;
	guild_name: string;

	on_poll_edited_func?: OnPollEditFunction;
	on_unmount?: Function;

	poll_channel_id: string | null;
	guild_timezone: string;
	use_24h_clock: boolean;
	user_timezone?: string;
	is_premium: boolean;
	can_select_channel: boolean;
	guild_data: GuildData;
	guild_default_single_vote_per_user: boolean;
	guild_default_allow_anyone_to_add: boolean;
	guild_default_poll_embed_color: string | null;
	are_polls_default_reaction_based: boolean;

	roles: Mentionable[];
	all_channel_data: SeshTextChannelData[];

	is_edit: boolean;
	edit_data?: PollEditData;

	err?: string;
	auth_token?: DiscordAuthToken;
	fragment?: URLSearchParams;
}

export interface MembersAndRolesMentionables {
	members: Mentionable[];
	roles: Mentionable[];
	longest_name_length: number;
}

export interface ISendMessageToChannelParams {
	guild_id: string;
	channel_id: string;
	content?: string;
	embed?: object;
}

export interface IPollCreationParams {
	user_id: string;
	guild_id: string;
	options: PollOption[];
	channel_id: string;
	title: string;
	description: string;
	end_time?: string;
	color?: string;
	poll_message_id?: string;
	allow_anyone_to_add: boolean;
	eligible_role_ids: string[];
	image_url: string;
	single_vote_per_user: boolean;
	is_anonymous: boolean;
	user_mentions_ids: string[];
	role_mention_ids: string[];
	forum_tag_ids: string[];

	poll_type: PollType;

	access_token: string;
	token_type: string;

	uuid?: string;
}

export interface ICacheAccessTokenParams {
	access_token: string;
	token_type: string;
	expires_in: number;
}

export interface PollOption {
	name: string;
	is_newly_added: boolean;
}

export interface IGetGuildDefaultOptionsFromDMArgs {
	guild_id: string;
	user_id: string;
	channel_id: string;
	author_display_name: string;
	from_url?: boolean;
}

export enum MentionTypes {
	User,
	Role,
	Channel,
}

export interface Mentionable {
	name: string;
	id: string;
	type: MentionTypes;
	discriminator?: string;
	color?: string;
	avatar_url?: string;
	disabled?: boolean;
	can_sesh_add_role?: boolean;
	can_user_add_role?: boolean;
	is_managed_role?: boolean;
	is_everyone_or_here?: boolean;
}

export interface EventCreateMentions {
	create_user_mentions: string[];
	create_role_mentions: string[];
}

interface IEventBaseMessage {
	uuid?: string;

	guild_id: string;
	event_name: string;
	start_time_str: string;
	end_time_str: string;
	rrule_string: string;
	exdates: string[];
	rdates: RDateDescriptor[];
	allow_maintain_repeat_rsvp: boolean;
	user_id: string;
	event_description: string;
	rsvp_options: OptionPreset;
	color: string;
	mention_data: EventCreateMentions;
	server_reminders: ServerReminderDescriptor[];
	eligible_role_ids: string[];
	channel_id: string;
	forum_tag_ids: string[];

	hide_attendees: boolean;
	image_url: string;
	event_timezone: string;
	token_type: string;
	access_token: string;

	rsvp_lock_before_ms: number | null;

	date_input_description?: {
		input: string;
		timezone: string;
	};
}

export interface ISendEditEventMessage extends IEventBaseMessage {
	message_id: string;
	inform_users_of_edit: boolean;
	native_event_data: NativeEventData;
	thread_settings: ThreadSettings | undefined;
	attendee_role_data: AttendeeRoleDataWithIDs;
}

export interface ISendCreateEventMessage extends IEventBaseMessage {
	native_event_override_data: OverridableSetting<NativeEventData>;
	attendee_role_override_data: OverridableSetting<AttendeeRoleDataWithIDs>;
	thread_settings: OverridableSetting<ThreadSettings>;
}

interface OverridableSetting<T> {
	is_overriding_server_default: boolean;
	value: T;
}

export interface AttendeeRoleData {
	roles: Mentionable[];
	role_add_time: RoleOnRSVPAddTime;
	remove_on_end: boolean;
}

export interface AttendeeRoleDataWithIDs {
	role_ids: string[];
	role_add_time: RoleOnRSVPAddTime;
	remove_on_end: boolean;
}

export interface IWebConfirmationData {
	is_success: boolean;
	error?: string;
}

export interface IWebEventCreateConfirmationData extends IWebConfirmationData {
	message_id?: string;
	link?: string;
}

export enum ShardToManagerCommandType {
	RestartAllShards,
	RestartSingleShard,
	PrintMemory,
}

export interface IShardToManagerCommand {
	shard_to_manager_command_type: ShardToManagerCommandType;
	args?: unknown;

	response_shard_id?: number;
	response_guild_id?: string;
	response_channel_id?: string;
}

export interface IShardCommand {
	command_type: CommandType;
	target_shard_id: number;
	args: Object | null;
}

export enum CommandType {
	SendGuildTimezoneChanged,
	CreateEventFromWeb,
	EditEventFromWeb,
	GetAddedPollOptionFromDM,
	UpdatePollWithExtraOption,
	GetGuildDefaultOptionsFromDM,
	SetGuildDefaultOptionsFromDM,
	CreatePollFromWeb,
	EditPollFromWeb,
	SendMessageToChannel,
}

export function instance_of_shard_to_manager_command(
	object: Object
): object is IShardToManagerCommand {
	return "shard_to_manager_command_type" in object;
}
